import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ArrowRight, 
  Sparkles, 
  MessageSquare, 
  Users, 
  HelpCircle,
  Zap,
  MessageCircle,
  GitBranch
} from 'lucide-react';

// Situation template chips
const SITUATION_TEMPLATES = [
  {
    id: 'difficult_conversation',
    label: 'Difficult conversation',
    icon: MessageSquare,
    prompt: "I need to have a difficult conversation with someone at work about...",
    color: 'red'
  },
  {
    id: 'unclear_expectations',
    label: 'Unclear expectations',
    icon: HelpCircle,
    prompt: "I'm not sure what my manager expects when they say...",
    color: 'amber'
  },
  {
    id: 'team_friction',
    label: 'Team issue',
    icon: Users,
    prompt: "There's tension in my team because...",
    color: 'purple'
  },
  {
    id: 'stakeholder_conflict',
    label: 'Stakeholder conflict',
    icon: Zap,
    prompt: "A key stakeholder and I don't see eye to eye on...",
    color: 'orange'
  },
  {
    id: 'feedback_received',
    label: 'Processing feedback',
    icon: MessageCircle,
    prompt: "I received feedback that... and I'm not sure how to respond",
    color: 'green'
  },
  {
    id: 'decision_stuck',
    label: 'Decision to make',
    icon: GitBranch,
    prompt: "I'm going back and forth on whether to...",
    color: 'blue'
  }
];

const ClarityHero = ({ series }) => {
  const [input, setInput] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleTemplateClick = (template) => {
    setInput(template.prompt);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      const message = input.trim();
      // Store the initial message for the demo chat
      sessionStorage.setItem('clarity_initial_message', message);
      
      // Scroll to demo chat
      const demoSection = document.getElementById('clarity-demo');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
        
        // Dispatch custom event after a short delay to ensure scroll completes
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('clarity-hero-submit', { 
            detail: { message } 
          }));
        }, 300);
      }
    }
  };

  const accentColor = series?.accentColor || '#dc2626';

  return (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden pt-20">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-100 via-stone-50 to-amber-50/50">
        <div 
          className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: accentColor }}
        />
        <div 
          className="absolute bottom-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: accentColor }}
        />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="h-full w-full" style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.2) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <Badge 
            className="mb-6 border"
            style={{
              backgroundColor: `${accentColor}10`,
              color: accentColor,
              borderColor: `${accentColor}30`
            }}
          >
            <Sparkles className="w-3 h-3 mr-1.5" />
            Think clearly. Act confidently.
          </Badge>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="text-slate-900">Tricky situation </span>
            <span style={{ color: accentColor }}>at work?</span>
            <br />
            <span className="text-slate-700 text-3xl sm:text-4xl lg:text-5xl">
              Get a clearer way to think about it.
            </span>
          </h1>

          {/* Subhead */}
          <p className="text-lg lg:text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            Describe what's happening. Walk away with a structured approach. 
            <span className="font-medium text-slate-700"> No signup required to start.</span>
          </p>

          {/* Main Input Box */}
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="relative max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="What situation is on your mind?"
                  rows={3}
                  className="w-full px-6 py-5 text-lg text-slate-800 placeholder-slate-400 resize-none focus:outline-none"
                  data-testid="clarity-hero-input"
                />
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1.5 transition-colors"
                    data-testid="clarity-show-templates"
                  >
                    <span>Examples</span>
                    <ArrowRight className={`w-3.5 h-3.5 transition-transform ${showTemplates ? 'rotate-90' : ''}`} />
                  </button>
                  <Button
                    type="submit"
                    disabled={!input.trim()}
                    className="px-6 py-2 text-white"
                    style={{ backgroundColor: accentColor }}
                    data-testid="clarity-hero-submit"
                  >
                    Think it through
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Template Dropdown */}
              {showTemplates && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-slate-200 p-3 z-20" data-testid="clarity-templates-dropdown">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-2">Common situations</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SITUATION_TEMPLATES.map((template) => {
                      const Icon = template.icon;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => handleTemplateClick(template)}
                          className="flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors group"
                          data-testid={`clarity-template-${template.id}`}
                        >
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${accentColor}10` }}
                          >
                            <Icon className="w-4 h-4" style={{ color: accentColor }} />
                          </div>
                          <span className="group-hover:text-slate-900">{template.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Quick template chips (always visible) */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {SITUATION_TEMPLATES.slice(0, 4).map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                className="px-4 py-2 bg-white/80 hover:bg-white border border-slate-200 hover:border-slate-300 rounded-full text-sm text-slate-600 hover:text-slate-800 transition-all shadow-sm hover:shadow"
                data-testid={`clarity-chip-${template.id}`}
              >
                {template.label}
              </button>
            ))}
          </div>

          {/* Trust note */}
          <p className="text-sm text-slate-500">
            Your reflections are private. We never share your data.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ClarityHero;
